import { useAuth } from "../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LogOut, Moon, Sun, ShieldCheck, User, Activity, Search, ListChecks } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CommandPalette } from "./CommandPalette";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("pd-theme") !== "light");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("pd-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const [dark, setDark] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const loc = useLocation();

  const { data: wl } = useQuery({
    queryKey: ["worklist-count"],
    queryFn: async () => (await api.get(`/worklist?limit=1`)).data,
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const reviewCount = wl?.total_open || 0;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto max-w-[1400px] px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-home" className="flex items-center gap-2.5 group">
            <div className="grid h-8 w-8 place-items-center rounded bg-primary text-primary-foreground">
              <Activity size={18} strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-lg font-extrabold tracking-tight">PayDelta</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground -mt-0.5">
                Payroll Variance Audit
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <button
              data-testid="global-search-trigger"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex items-center gap-2 rounded-md border border-border bg-background px-3 h-9 text-sm text-muted-foreground transition-colors hover:text-foreground hover:border-foreground/30"
            >
              <Search size={15} />
              <span>Search…</span>
              <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">⌘K</kbd>
            </button>
            <Button asChild variant="ghost" size="icon" data-testid="nav-worklist" aria-label="Needs Review" className="relative">
              <Link to="/worklist">
                <ListChecks size={18} />
                {reviewCount > 0 && (
                  <span
                    data-testid="worklist-badge"
                    className="absolute -top-1 -right-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-neg px-1 text-[10px] font-bold leading-none text-white ring-2 ring-card"
                  >
                    {reviewCount > 99 ? "99+" : reviewCount}
                  </span>
                )}
              </Link>
            </Button>
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="icon"
              onClick={() => setDark(!dark)}
              aria-label="Toggle theme"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu" variant="outline" className="gap-2 rounded-md">
                  {user?.role === "admin" ? <ShieldCheck size={16} /> : <User size={16} />}
                  <span className="max-w-[160px] truncate text-sm">{user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-semibold">{user?.name || "User"}</span>
                  <span className="text-xs font-normal text-muted-foreground uppercase tracking-wide">
                    {user?.role}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin">
                      <ShieldCheck size={16} className="mr-2" /> User Management
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main key={loc.pathname} className="mx-auto max-w-[1400px] px-6 py-8 animate-fade-up">
        {children}
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
